
import type { Instance } from '@empathybroker/tsclient-play/dist/models/instance/instance'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import { PermissionLevelEnum } from '@empathybroker/tsclient-play/dist/enums/permission-level.enum'
import {
  APP_CONSTS,
  AUTH_CONSTS,
  CONFIG_CONSTS,
  CONTAINER,
  CONTEXT_CONSTS,
  FeatureIdEnum,
  ROUTER_CONSTS,
} from '@empathyco/playboard-core/dist/src'
import { mapActions, mapGetters } from '@empathyco/playboard-core/dist/src/composables'
import { TYPES } from '@empathyco/playboard-core/dist/src/config/inversify.types'
import { computed, defineComponent, getCurrentInstance, watch } from 'vue'

export default defineComponent({
  name: 'App',
  provide(): Record<string, any> {
    return {
      [TYPES.ObjectIdService]: CONTAINER.get(TYPES.ObjectIdService),
    }
  },
  setup() {
    const ctx = getCurrentInstance()
    if (!ctx) {
      throw new Error("'ctx' is not defined")
    }

    const { isLoggedIn, token } = mapGetters(ctx.proxy.$store, AUTH_CONSTS.moduleName, {
      isLoggedIn: AUTH_CONSTS.getters.isLoggedIn,
      token: AUTH_CONSTS.getters.token,
    })

    const { alreadyLoggedInOnce } = mapGetters<boolean>(ctx.proxy.$store, APP_CONSTS.moduleName, {
      alreadyLoggedInOnce: APP_CONSTS.getters.alreadyLoggedInOnce,
    })

    const isVersionVisible = computed<boolean>(
      () =>
        ctx.proxy.$route.name === ROUTER_CONSTS.routes.login ||
        ctx.proxy.$route.name === ROUTER_CONSTS.routes.home,
    )

    const { setAlreadyLoggedInOnce } = mapActions(ctx.proxy.$store, APP_CONSTS.moduleName, {
      setAlreadyLoggedInOnce: APP_CONSTS.actions.setAlreadyLoggedInOnce,
    })

    function onLogin(success: boolean): void {
      if (success) {
        ctx.proxy.$router.replace({
          ...ctx.proxy.$route,
          name: ROUTER_CONSTS.routes.home,
        })
      }
    }

    watch(
      isLoggedIn,
      async value => {
        // With this we disable the first login dialog until we change it to welcome new users,
        // no matter which project it is
        if (value && !alreadyLoggedInOnce.value) {
          await setAlreadyLoggedInOnce(true)
        }
      },
      { immediate: true },
    )
    const { context } = mapGetters(ctx.proxy.$store, CONTEXT_CONSTS.moduleName, {
      context: CONTEXT_CONSTS.getters.context,
    })
    const { instance } = mapGetters<Instance>(ctx.proxy.$store, CONFIG_CONSTS.moduleName, {
      instance: CONFIG_CONSTS.getters.currentInstance,
    })

    const hasInnovationsPermissions = computed(() => {
      return instance.value?.domains[DomainEnum.GLOBAL].permissions.some(permission => {
        return (
          permission.id === FeatureIdEnum.INNOVATIONS &&
          permission.level !== PermissionLevelEnum.NONE
        )
      })
    })

    watch(
      hasInnovationsPermissions,
      async value => {
        if (value) {
          const instanceId: string = context.value.instanceId
          await window.chatWidget?.mount()
          await window.chatWidget?.setContext({ instanceId, userToken: token.value })
        } else {
          await window.chatWidget?.unmount()
          await window.chatWidget?.setContext({ instanceId: undefined, userToken: undefined })
        }
      },
      { immediate: true },
    )

    return {
      isVersionVisible,
      onLogin,
    }
  },
})
